import React, { useState } from 'react';
import { 
  Typography, 
  Card, 
  Table, 
  Tag, 
  Space, 
  Button, 
  Row, 
  Col, 
  Statistic, 
  DatePicker, 
  Select,
  Modal,
  Breadcrumb,
  Descriptions,
  Divider,
  Tooltip,
  message
} from 'antd';
import { 
  DollarOutlined, 
  UserOutlined, 
  FileTextOutlined,
  EyeOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface CommissionRecord {
  key: string;
  employeeName: string;
  phone: string;
  customerName: string;
  orderNo: string;
  orderAmount: number;
  commissionAmount: number;
  commissionType: 'order' | 'sharing' | 'system';
  status: 'pending' | 'settled';
  createdAt: string;
  sharedWith?: {
    employeeName: string;
    amount: number;
  }[];
}

const CommissionManagement: React.FC = () => {
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState<CommissionRecord | null>(null);

  // 佣金记录数据
  const [commissionData, setCommissionData] = useState<CommissionRecord[]>([
    {
      key: '1',
      employeeName: '张三',
      phone: '13800138000',
      customerName: '客户A',
      orderNo: 'ORD20230512001',
      orderAmount: 2000,
      commissionAmount: 200,
      commissionType: 'order',
      status: 'pending',
      createdAt: '2023-05-12 09:30:15',
      sharedWith: []
    },
    {
      key: '2',
      employeeName: '李四',
      phone: '13900139000',
      customerName: '客户B',
      orderNo: 'ORD20230515002',
      orderAmount: 3500,
      commissionAmount: 350,
      commissionType: 'order',
      status: 'settled',
      createdAt: '2023-05-15 14:22:45',
      sharedWith: [
        { employeeName: '王五', amount: 70 }
      ]
    },
    {
      key: '3',
      employeeName: '王五',
      phone: '13700137000',
      customerName: '客户C',
      orderNo: 'ORD20230518003',
      orderAmount: 1800,
      commissionAmount: 180,
      commissionType: 'sharing',
      status: 'settled', // 分账收入类型都是已结算状态
      createdAt: '2023-05-18 16:05:32',
      sharedWith: []
    },
    {
      key: '4',
      employeeName: '赵六',
      phone: '13600136000',
      customerName: '客户D',
      orderNo: 'ORD20230520004',
      orderAmount: 0, // 系统赠送没有订单金额
      commissionAmount: 120,
      commissionType: 'system',
      status: 'pending',
      createdAt: '2023-05-20 10:45:18',
      sharedWith: []
    },
    {
      key: '5',
      employeeName: '赵六',
      phone: '13600136000',
      customerName: '客户E',
      orderNo: 'ORD20230525005',
      orderAmount: 0,
      commissionAmount: 90,
      commissionType: 'sharing',
      status: 'settled', // 分账收入类型都是已结算状态
      createdAt: '2023-05-25 11:38:24',
      sharedWith: []
    }
  ]);

  // 显示详情模态框
  const handleViewDetail = (record: CommissionRecord) => {
    setSelectedCommission(record);
    setDetailModalVisible(true);
  };

  // 获取佣金类型显示文本
  const getCommissionTypeText = (type: string) => {
    switch (type) {
      case 'order':
        return '订单分佣';
      case 'sharing':
        return '分账收入';
      case 'system':
        return '系统赠送';
      default:
        return type;
    }
  };

  // 获取佣金状态显示文本
  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return '待结算';
      case 'settled':
        return '已结算';
      default:
        return status;
    }
  };

  // 手机号码中间四位加密显示
  const maskPhoneNumber = (phone: string) => {
    if (!phone || phone.length < 8) return phone;
    const prefix = phone.substring(0, 3);
    const suffix = phone.substring(7);
    return `${prefix}****${suffix}`;
  };

  // 佣金记录表格列
  const commissionColumns: ColumnsType<CommissionRecord> = [
    {
      title: '员工姓名',
      dataIndex: 'employeeName',
      key: 'employeeName',
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => (
        <Tooltip title={phone}>
          {maskPhoneNumber(phone)}
        </Tooltip>
      ),
    },
    {
      title: '客户姓名',
      dataIndex: 'customerName',
      key: 'customerName',
    },
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      render: (orderNo, record) => {
        // 系统赠送类型可能没有订单号
        if (record.commissionType === 'system' && !orderNo) {
          return '-';
        }
        return orderNo;
      }
    },
    {
      title: '订单金额(元)',
      dataIndex: 'orderAmount',
      key: 'orderAmount',
      render: (amount, record) => {
        // 系统赠送类型可能没有订单金额
        if (record.commissionType === 'system' && amount === 0) {
          return '-';
        }
        return `￥${amount.toFixed(2)}`;
      }
    },
    {
      title: '佣金金额(元)',
      dataIndex: 'commissionAmount',
      key: 'commissionAmount',
      render: (amount) => `￥${amount.toFixed(2)}`,
    },
    {
      title: '佣金类型',
      dataIndex: 'commissionType',
      key: 'commissionType',
      render: (type) => {
        let color = 'blue';
        let text = '订单分佣';
        
        if (type === 'sharing') {
          color = 'purple';
          text = '分账收入';
        } else if (type === 'system') {
          color = 'green';
          text = '系统赠送';
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'processing';
        let text = '待结算';
        
        if (status === 'settled') {
          color = 'success';
          text = '已结算';
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '分佣时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button 
          type="link" 
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb 
        style={{ marginBottom: 16 }}
        items={[
          { title: '佣金管理' },
          { title: '佣金记录' }
        ]} 
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card>
            <Statistic 
              title="待结算佣金" 
              value={commissionData.filter(item => item.status === 'pending').reduce((acc, curr) => acc + curr.commissionAmount, 0)} 
              precision={2}
              prefix="￥"
              valueStyle={{ color: '#529C8A' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <Statistic 
              title="已结算佣金" 
              value={commissionData.filter(item => item.status === 'settled').reduce((acc, curr) => acc + curr.commissionAmount, 0)}
              precision={2}
              prefix="￥"
              valueStyle={{ color: '#529C8A' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Space>
            <RangePicker placeholder={['开始日期', '结束日期']} showTime />
            <Select defaultValue="all" style={{ width: 120 }}>
              <Option value="all">所有状态</Option>
              <Option value="pending">待结算</Option>
              <Option value="settled">已结算</Option>
            </Select>
            <Select defaultValue="all" style={{ width: 120 }}>
              <Option value="all">所有类型</Option>
              <Option value="order">订单分佣</Option>
              <Option value="sharing">分账收入</Option>
              <Option value="system">系统赠送</Option>
            </Select>
            <Button type="primary" style={{ backgroundColor: '#529C8A', borderColor: '#529C8A' }}>
              查询
            </Button>
          </Space>
        </div>

        <Table 
          columns={commissionColumns} 
          dataSource={commissionData} 
          pagination={{ defaultPageSize: 10 }}
        />
      </Card>

      {/* 佣金详情模态框 */}
      <Modal
        title="佣金详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {selectedCommission && (
          <>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="员工姓名" span={1}>
                {selectedCommission.employeeName}
              </Descriptions.Item>
              <Descriptions.Item label="联系电话" span={1}>
                {selectedCommission.phone}
              </Descriptions.Item>
              <Descriptions.Item label="客户姓名" span={1}>
                {selectedCommission.customerName}
              </Descriptions.Item>
              <Descriptions.Item label="分佣时间" span={1}>
                {selectedCommission.createdAt}
              </Descriptions.Item>
              <Descriptions.Item label="订单号" span={1}>
                {selectedCommission.commissionType === 'system' && !selectedCommission.orderNo ? '-' : selectedCommission.orderNo}
              </Descriptions.Item>
              <Descriptions.Item label="订单金额" span={1}>
                {selectedCommission.commissionType === 'system' && selectedCommission.orderAmount === 0 ? 
                  '-' : `￥${selectedCommission.orderAmount.toFixed(2)}`}
              </Descriptions.Item>
              <Descriptions.Item label="佣金金额" span={1}>
                <Text strong style={{ color: '#529C8A' }}>
                  ￥{selectedCommission.commissionAmount.toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="佣金类型" span={1}>
                <Tag color={
                  selectedCommission.commissionType === 'order' ? 'blue' :
                  selectedCommission.commissionType === 'sharing' ? 'purple' : 'green'
                }>
                  {getCommissionTypeText(selectedCommission.commissionType)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态" span={1}>
                <Tag color={selectedCommission.status === 'pending' ? 'processing' : 'success'}>
                  {getStatusText(selectedCommission.status)}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
            
            {selectedCommission.sharedWith && selectedCommission.sharedWith.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <Divider orientation="left">分账明细</Divider>
                <Table 
                  dataSource={selectedCommission.sharedWith.map((item, index) => ({ 
                    ...item, 
                    key: index 
                  }))}
                  columns={[
                    {
                      title: '员工姓名',
                      dataIndex: 'employeeName',
                      key: 'employeeName',
                    },
                    {
                      title: '分账金额',
                      dataIndex: 'amount',
                      key: 'amount',
                      render: (amount) => `￥${amount.toFixed(2)}`,
                    }
                  ]}
                  pagination={false}
                  size="small"
                />
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default CommissionManagement; 